// client/src/components/pages/TelegramBI/Analytics/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePolling from '../../../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaBolt,
  FaUsers,
  FaSyncAlt,
  FaDatabase,
  FaFire,
  FaPercentage,
  FaPaperPlane,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import QueryPerformanceChart from './QueryPerformanceChart';
import DeliveryStatsChart from './DeliveryStatsChart';
import SystemHealthCard from './SystemHealthCard';
import UserActivityChart from './UserActivityChart';
import PageHeader from '../../../common/PageHeader';
import SegmentedControl from '../../../common/SegmentedControl';

const TIME_RANGES = [
  { value: '1d', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
];

const MetricCard = ({ label, value, sub, icon: Icon, iconBg }) => (
  <div className="bg-surface rounded-lg border border-gray-200 p-5 hover:border-gray-300 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg || 'bg-gray-100 text-gray-500'}`}
      >
        <Icon className="text-sm" />
      </div>
    </div>
    <div className="text-2xl font-semibold text-gray-900 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
  </div>
);

const Analytics = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');

  const loadAnalytics = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      try {
        const [dashboardRes, systemRes, healthRes] = await Promise.allSettled([
          api.telegramBI.analytics.getDashboard({ timeRange }),
          api.telegramBI.analytics.getSystem({ timeRange }),
          api.telegramBI.analytics.getHealth(),
        ]);

        if (dashboardRes.status === 'fulfilled') {
          setDashboardData(dashboardRes.value.data?.data || dashboardRes.value.data);
        }
        if (systemRes.status === 'fulfilled') {
          setSystemData(systemRes.value.data?.data || systemRes.value.data);
        }
        if (healthRes.status === 'fulfilled') {
          setHealthData(healthRes.value.data?.data || healthRes.value.data);
        }

        if (isRefresh) {
          toast.success('Analytics refreshed');
        }
      } catch (error) {
        console.error('Error loading analytics:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [timeRange]
  );

  useEffect(() => {
    setLoading(true);
    loadAnalytics();
  }, [loadAnalytics]);

  // Auto-refresh every 60 seconds, paused while this tab is in the background.
  usePolling(
    useCallback(() => loadAnalytics(false), [loadAnalytics]),
    60000
  );

  const handleRefresh = () => {
    loadAnalytics(true);
  };

  // Extract data with safe defaults
  const queries = dashboardData?.queries || {};
  const templates = dashboardData?.templates || {};
  const schedules = dashboardData?.schedules || {};
  const trends = dashboardData?.trends || [];
  const topQueries = dashboardData?.topQueries || [];

  const system = systemData?.system || {};
  const performance = systemData?.performance || {};
  const activity = systemData?.activity || {};
  const errors = systemData?.errors || {};

  // Calculate metrics
  const totalExecutions = queries.total_executions || system.total_executions || 0;
  const successfulExec = queries.successful_executions || 0;
  const failedExec = queries.failed_executions || 0;
  const successRate =
    totalExecutions > 0
      ? ((successfulExec / totalExecutions) * 100).toFixed(1)
      : (performance.success_rate || 0).toFixed(1);
  const avgQueryTime = queries.avg_execution_time || performance.avg_query_time || 0;
  const cacheHitRate =
    queries.cache_hits != null && totalExecutions > 0
      ? ((queries.cache_hits / totalExecutions) * 100).toFixed(1)
      : (performance.cache_hit_rate || 0).toFixed(1);
  const activeUsers = activity.active_users || system.total_users || 0;
  const messagesSent = system.total_messages || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-surface border-b border-slate-200">
          <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
            <div className="animate-pulse flex items-center gap-4">
              <div className="hidden sm:block w-11 h-11 bg-gray-200 rounded-xl" />
              <div className="h-6 bg-gray-200 rounded w-48" />
            </div>
          </div>
        </div>
        <div className="p-3 sm:p-6 max-w-[1800px] mx-auto animate-pulse space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-28 bg-gray-200 rounded-lg" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded-lg" />
            <div className="h-80 bg-gray-200 rounded-lg" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg" />
            <div className="h-64 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaChartBar}
        title="Analytics"
        subtitle="Telegram BI performance metrics and insights"
        toolbar={
          <SegmentedControl
            ariaLabel="Time range"
            value={timeRange}
            onChange={setTimeRange}
            options={TIME_RANGES}
          />
        }
        actions={
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
          >
            <FaSyncAlt
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </button>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        {/* Key Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <MetricCard
            label="Total Executions"
            value={totalExecutions.toLocaleString()}
            sub={queries.unique_queries ? `${queries.unique_queries} unique queries` : null}
            icon={FaBolt}
            iconBg="bg-amber-50 text-amber-600"
          />
          <MetricCard
            label="Success Rate"
            value={`${successRate}%`}
            sub={failedExec > 0 ? `${failedExec} failed` : 'No failures'}
            icon={FaCheckCircle}
            iconBg="bg-emerald-50 text-emerald-600"
          />
          <MetricCard
            label="Avg Query Time"
            value={`${typeof avgQueryTime === 'number' ? avgQueryTime.toFixed(0) : avgQueryTime}ms`}
            sub={performance.max_query_time ? `Max: ${performance.max_query_time}ms` : null}
            icon={FaClock}
            iconBg="bg-violet-50 text-violet-600"
          />
          <MetricCard
            label="Cache Hit Rate"
            value={`${cacheHitRate}%`}
            sub={queries.cache_hits != null ? `${queries.cache_hits} cache hits` : null}
            icon={FaFire}
            iconBg="bg-orange-50 text-orange-600"
          />
          <MetricCard
            label="Active Users"
            value={activeUsers}
            sub={activity.active_days ? `${activity.active_days} active days` : null}
            icon={FaUsers}
            iconBg="bg-blue-50 text-blue-600"
          />
          <MetricCard
            label="Messages Sent"
            value={messagesSent.toLocaleString()}
            sub={system.total_schedules ? `${system.total_schedules} schedules` : null}
            icon={FaPaperPlane}
            iconBg="bg-[#0088cc]/10 text-[#0088cc]"
          />
        </div>

        {/* Middle Section: Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <QueryPerformanceChart data={trends} loading={false} />
          <DeliveryStatsChart
            data={{
              total_executions: totalExecutions,
              successful_executions: successfulExec,
              failed_executions: failedExec,
              cache_hits: queries.cache_hits || 0,
            }}
            loading={false}
          />
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SystemHealthCard data={healthData} loading={false} />
          <UserActivityChart data={topQueries} loading={false} />
        </div>

        {/* Top Queries Table */}
        <div className="bg-surface rounded-lg border border-gray-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FaDatabase className="text-primary-500 text-sm" />
              <h2 className="text-sm font-semibold text-gray-900">
                Top Queries by Execution Count
              </h2>
            </div>
            <span className="text-xs text-gray-500">
              {topQueries.length > 0 ? `Showing top ${Math.min(topQueries.length, 10)}` : 'No data'}
            </span>
          </div>
          {topQueries.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <FaDatabase className="mx-auto text-2xl text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">
                No query execution data available for this period
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Queries will appear here once they have been executed
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider w-8">
                      #
                    </th>
                    <th className="text-left px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Query Name
                    </th>
                    <th className="text-right px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Executions
                    </th>
                    <th className="text-right px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time
                    </th>
                    <th className="text-right px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Last Executed
                    </th>
                    <th className="text-right px-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider w-32">
                      Volume
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {topQueries.slice(0, 10).map((query, index) => {
                    const maxExec = topQueries[0]?.execution_count || 1;
                    const barWidth = ((query.execution_count || 0) / maxExec) * 100;
                    return (
                      <tr
                        key={query.query_id || index}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-5 py-3 text-xs text-gray-400 font-medium">{index + 1}</td>
                        <td className="px-5 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {query.query_name || `Query ${query.query_id}`}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {(query.execution_count || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span
                            className={`text-sm font-medium ${
                              (query.avg_execution_time || 0) > 5000
                                ? 'text-red-600'
                                : (query.avg_execution_time || 0) > 2000
                                  ? 'text-amber-600'
                                  : 'text-gray-600'
                            }`}
                          >
                            {typeof query.avg_execution_time === 'number'
                              ? query.avg_execution_time.toFixed(0)
                              : query.avg_execution_time || 0}
                            ms
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-xs text-gray-500">
                            {query.last_executed
                              ? new Date(query.last_executed).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '--'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="h-full bg-primary-500 rounded-full transition-all duration-500"
                                style={{ width: `${barWidth}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* System Performance Summary */}
        {(errors.total_errors > 0 || performance.success_rate) && (
          <div className="mt-6 bg-surface rounded-lg border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaChartBar className="text-primary-500" />
              System Performance Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {performance.min_query_time != null && (
                <div className="text-center">
                  <div className="text-xl font-bold text-emerald-600">
                    {performance.min_query_time}ms
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Min Query Time</div>
                </div>
              )}
              {performance.max_query_time != null && (
                <div className="text-center">
                  <div className="text-xl font-bold text-red-600">
                    {performance.max_query_time}ms
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Max Query Time</div>
                </div>
              )}
              {errors.total_errors != null && (
                <div className="text-center">
                  <div className="text-xl font-bold text-amber-600">{errors.total_errors}</div>
                  <div className="text-xs text-gray-500 mt-1">Total Errors</div>
                </div>
              )}
              {errors.unique_errors != null && (
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-700">{errors.unique_errors}</div>
                  <div className="text-xs text-gray-500 mt-1">Unique Errors</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
